import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';

import {
	type BuildingBlockDms,
	MAX_DISPLAYED_DM_CARDS_ON_PETITION,
	type NormalizedDecisionMaker,
} from '../../shared/types';

import {
	formatAllBuckets,
	getLegislativeBodies,
	getPrimaryDms,
	normalizeDms,
	sortAllBuildingBlocks,
	sortAllDms,
} from './utils';

type Result = ModelHookResult<{
	sortedAllDms: BuildingBlockDms;
	totalDmCount: number;
	displayViewAllButton: boolean;
}>;

export function useDecisionMakers(): Result {
	const {
		data: {
			petition: {
				primaryDecisionMakerConnection: { nodes: dms, totalCount: totalDmCount },
				dmResponsesConnection: { nodes: allDmResponses },
			},
		},
	} = usePetitionDetailsPageData();

	const normalizedAll = normalizeDms(dms, allDmResponses);
	const politiciansOnly = normalizedAll.filter((dm: NormalizedDecisionMaker) => dm.type === 'POLITICIAN');

	// One bucket: primary DMs
	const primaryDMs = getPrimaryDms(politiciansOnly);

	// One bucket: legislative bodies
	const primaryDmIds = new Set(primaryDMs.map((dm) => dm.id));
	const politiciansExcludingPrimaries = politiciansOnly.filter((dm) => !primaryDmIds.has(dm.id));
	const legislativeBodiesList = getLegislativeBodies(politiciansExcludingPrimaries);

	const legislativeBodiesWithDmsSorted = legislativeBodiesList.map((body) => {
		const sortedLBDMs = sortAllDms(body.groupDms);

		const hasMissing = sortedLBDMs.some((dm) => !dm.location);
		const uniqueLocations = [...new Set(sortedLBDMs.map((dm) => dm.location))];
		const location = !hasMissing && uniqueLocations.length === 1 ? uniqueLocations[0] : null;

		return {
			title: body.groupTitle,
			location,
			responseCount: sortedLBDMs.filter((x) => x.response).length,
			decisionMakers: sortedLBDMs,
		};
	});
	const legislativeBodyDmIds = new Set(
		legislativeBodiesWithDmsSorted.flatMap((body) => body.decisionMakers.map((dm) => dm.id)),
	);

	// One bucket: politicians that are neither primary nor in a legislative body
	const individualPoliticians = politiciansOnly.filter((dm) => {
		return !legislativeBodyDmIds.has(dm.id) && !primaryDmIds.has(dm.id);
	});

	// One bucket: remaining DMs
	const remainingDMs = normalizedAll.filter((dm) => dm.type !== 'POLITICIAN');

	const allFormattedBucketsOfDms = formatAllBuckets(
		primaryDMs,
		legislativeBodiesWithDmsSorted,
		individualPoliticians,
		remainingDMs,
	);
	const sortedAllDms = sortAllBuildingBlocks(allFormattedBucketsOfDms);

	return {
		data: {
			sortedAllDms,
			totalDmCount,
			displayViewAllButton: sortedAllDms.length > MAX_DISPLAYED_DM_CARDS_ON_PETITION,
		},
	};
}
